import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';
import { playerOrganisedEventPath, rememberPlayerNextPath } from '../utils/playerNextPath';

const Wrap = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
`;

const CoverFrame = styled.div`
  border-radius: 14px;
  margin-bottom: 1.25rem;
  overflow: hidden;
  background: ${(p) =>
    p.$placeholder ? 'linear-gradient(135deg, #043663, #0ea5e9)' : '#0b1220'};
  min-height: ${(p) => (p.$placeholder ? '220px' : '0')};
`;

const CoverImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
`;

const Meta = styled.p`
  margin: 0 0 1rem;
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const Desc = styled.div`
  white-space: pre-wrap;
  color: #334155;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const Cta = styled(Link)`
  display: inline-block;
  border: none;
  background: #043663;
  color: #fff;
  font-weight: 700;
  padding: 0.85rem 1.25rem;
  border-radius: 10px;
  text-decoration: none;
  font-size: 1rem;
`;

const Slot = styled.div`
  padding: 0.7rem 0.8rem;
  margin-bottom: 0.45rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
`;

async function fetchPublicEvent(id) {
  const res = await fetch(`/api/public/organised-events/${id}`);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.message || 'Event not found');
    err.status = res.status;
    throw err;
  }
  return body.data?.event || body.event || body.data;
}

function formatRange(start, end) {
  if (!start) return '—';
  const a = new Date(start).toLocaleString();
  return end ? `${a} → ${new Date(end).toLocaleString()}` : a;
}

export default function PublicOrganisedEventPage() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const { data: event, isLoading, isError } = useQuery(
    ['publicOrganisedEvent', eventId],
    () => fetchPublicEvent(eventId),
    { enabled: !!eventId, retry: false }
  );

  useSEO({
    title: event?.name ? `${event.name} — Helvetic Padel Series` : 'Event — Helvetic Padel Series',
    description: event?.description ? String(event.description).slice(0, 160) : 'Book your spot.',
  });

  const bookPath = playerOrganisedEventPath(eventId);
  useEffect(() => {
    if (eventId) rememberPlayerNextPath(bookPath);
  }, [eventId, bookPath]);
  const loginPath = `/login?next=${encodeURIComponent(bookPath)}`;
  const ctaTo = user?.type === 'player' ? bookPath : loginPath;
  const slots = Array.isArray(event?.slots) ? event.slots : [];
  const cover =
    event?.coverImageUrl ||
    (event?.hasCoverImage ? `/api/public/organised-events/${event.id}/cover-image` : null);

  if (isLoading) {
    return (
      <Wrap>
        <p>Loading…</p>
      </Wrap>
    );
  }

  if (isError || !event?.id) {
    return (
      <Wrap>
        <Title>Event not found</Title>
        <Meta>This event is not available. It may have been removed or is not published yet.</Meta>
        <Cta to="/login">Go to login</Cta>
      </Wrap>
    );
  }

  const ctaLabel =
    user?.type === 'player' ? 'Book this event' : 'Log in to book';

  return (
    <Wrap>
      {cover ? (
        <CoverFrame>
          <CoverImg src={cover} alt={event.name || ''} />
        </CoverFrame>
      ) : (
        <CoverFrame $placeholder />
      )}
      <Title>{event.name}</Title>
      <Meta>
        {event.clubName || 'Venue TBC'}
        {event.clubAddress ? ` · ${event.clubAddress}` : ''}
        <br />
        {slots.length > 1
          ? `${slots.length} start times — pick one when you book`
          : formatRange(event.startsAt, event.endsAt)}
        <br />
        {Number(event.priceChf) <= 0 ? 'Free' : `CHF ${Number(event.priceChf).toFixed(2)}`}
        {event.status === 'published'
          ? ` · ${event.remainingSpots} spots left`
          : event.status === 'full'
            ? ' · Full'
            : event.status === 'closed'
              ? ' · Bookings closed'
              : event.status === 'cancelled'
                ? ' · Cancelled'
                : ''}
      </Meta>
      {event.description ? <Desc>{event.description}</Desc> : null}
      {slots.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <Meta style={{ marginBottom: '0.5rem', fontWeight: 700, color: '#0f172a' }}>
            Start times
          </Meta>
          {slots.map((s, i) => (
            <Slot key={`${s.startsAt}-${i}`}>
              <strong>{s.label || new Date(s.startsAt).toLocaleString()}</strong>
              {s.label ? (
                <>
                  <br />
                  {formatRange(s.startsAt, s.endsAt)}
                </>
              ) : s.endsAt ? (
                <>
                  <br />
                  {`Ends ${new Date(s.endsAt).toLocaleString()}`}
                </>
              ) : null}
            </Slot>
          ))}
        </div>
      )}
      {event.status === 'published' ? <Cta to={ctaTo}>{ctaLabel}</Cta> : null}
    </Wrap>
  );
}
